import { Type, Expose, Exclude } from 'class-transformer'
import { DocumentSnapshot } from 'firebase/firestore'
import { _FirebaseDocument } from './Models'

export type CoherenceCheckItemStatus = 'todo' | 'done' | 'failed'
export type CoherenceCheckOverallStatus = 'pass' | 'fail' | 'partial'

/** Validation result for a single cross-referenced document. */
export class CrossReferenceValidation {
  @Expose()
  public referenced_document!: string
  @Expose()
  public exists!: boolean
  @Expose()
  public document_path?: string
  @Expose()
  public project_id?: string
  @Expose()
  public datasource_id?: string
  @Expose()
  public document_id?: string
  @Expose()
  public error_message?: string
}

/** Validation result for a single external link. */
export class LinkValidation {
  @Expose()
  public link_or_path!: string
  @Expose()
  public link_type!: string
  @Expose()
  public valid!: boolean
  @Expose()
  public accessible!: boolean
  @Expose()
  public error_message?: string
}

/** Sub-result for structure/naming validation (filename, file type, location). */
export class StructureNamingResult {
  @Expose()
  public passes!: boolean
  @Expose()
  public filename_valid!: boolean
  @Expose()
  public file_type_valid!: boolean
  @Expose()
  public file_location_valid!: boolean
  @Expose()
  public extracted_filename?: string
  @Expose()
  public extracted_file_type?: string
  @Expose()
  public extracted_file_location?: string
  @Expose()
  public issues: string[] = []
  @Expose()
  public notes?: string
}

/** Sub-result for version/signature validation (version, author, reviewer, approver, signatures). */
export class VersionSignatureResult {
  @Expose()
  public passes!: boolean
  @Expose()
  public version_number?: string
  @Expose()
  public version_valid!: boolean
  @Expose()
  public author?: string
  @Expose()
  public reviewer?: string
  @Expose()
  public approver?: string
  @Expose()
  public should_be_signed!: boolean
  @Expose()
  public is_signed!: boolean
  @Expose()
  public required_signatures: string[] = []
  @Expose()
  public present_signatures: string[] = []
  @Expose()
  public missing_signatures: string[] = []
  @Expose()
  public issues: string[] = []
  @Expose()
  public notes?: string
}

/** Sub-result for cross-references validation (referenced documents exist). */
export class CrossReferenceResult {
  @Expose()
  public passes!: boolean
  @Expose()
  @Type(() => CrossReferenceValidation)
  public referenced_documents: CrossReferenceValidation[] = []
  @Expose()
  public issues: string[] = []
  @Expose()
  public notes?: string
}

/** Sub-result for links/paths validation (external links validated). */
export class LinksPathsResult {
  @Expose()
  public passes!: boolean
  @Expose()
  @Type(() => LinkValidation)
  public external_links: LinkValidation[] = []
  @Expose()
  public issues: string[] = []
  @Expose()
  public notes?: string
}

export class CoherenceCheckResult {
  @Expose()
  @Type(() => StructureNamingResult)
  public structure_naming?: StructureNamingResult
  @Expose()
  @Type(() => VersionSignatureResult)
  public version_signature?: VersionSignatureResult
  @Expose()
  @Type(() => CrossReferenceResult)
  public cross_references?: CrossReferenceResult
  @Expose()
  @Type(() => LinksPathsResult)
  public links_paths?: LinksPathsResult
  @Expose()
  public overall_status?: CoherenceCheckOverallStatus
  @Expose()
  public summary?: string
}

@Exclude()
export class CoherenceCheck extends _FirebaseDocument {
  /** Full resource path: projects/<PID>/datasources/<DSID>/documents/<DOCID>. May be missing in legacy data. */
  @Expose()
  public document_path?: string
  @Expose()
  public status!: CoherenceCheckItemStatus
  @Expose()
  public filename?: string | null
  @Expose()
  @Type(() => CoherenceCheckResult)
  public result?: CoherenceCheckResult | null
  @Expose()
  public overall_status?: CoherenceCheckOverallStatus | null
  @Expose()
  public error?: string | null

  collection(): string {
    return 'coherence_checks'
  }

  static fromFirebase(o: DocumentSnapshot): CoherenceCheck | null {
    return _FirebaseDocument._fromFirebase(CoherenceCheck, o)
  }
}
